import { useEffect, useMemo, useRef, useState } from "react";

import {
  useCustomizationWorkbenchRuntime,
  type CustomizationProjectEditOptions,
  type CustomizationDispatcherNotificationResult,
} from "../../customization";
import {
  buildCustomizationMobileDemandGroups,
  buildCustomizationMobileEditModel,
  getCustomizationMobileRequiredStatus,
} from "../service";
import type {
  CustomizationMobileWorkbenchNotice,
  CustomizationMobileWorkbenchViewModel,
} from "../types";
import type { CustomizationProjectUpdatePayload } from "../../customization";

type CustomizationMobileWorkbenchRuntimeOptions = NonNullable<
  Parameters<typeof useCustomizationWorkbenchRuntime>[1]
>;

const mobileProjectEditOptionsPendingRequests = new Map<
  string,
  Promise<CustomizationProjectEditOptions | null>
>();

/** 管理手机临时需求定制页的数据、版本切换和通知状态。 */
export function useCustomizationMobileWorkbenchRuntime(
  projectId: string,
  options: CustomizationMobileWorkbenchRuntimeOptions = {},
) {
  const runtime = useCustomizationWorkbenchRuntime(projectId, options);
  const { project } = runtime;
  const [notice, setNotice] = useState<CustomizationMobileWorkbenchNotice | null>(null);
  const [projectEditOptions, setProjectEditOptions] = useState<CustomizationProjectEditOptions | null>(null);
  const [projectEditOptionsLoading, setProjectEditOptionsLoading] = useState(false);
  const [projectPointNames, setProjectPointNames] = useState<string[]>([]);
  const [projectPointNamesLoading, setProjectPointNamesLoading] = useState(false);
  const loadProjectEditOptionsRef = useRef(runtime.loadProjectEditOptions);
  const loadProjectPointNamesRef = useRef(runtime.loadProjectPointNames);
  const projectEditOptionsCacheRef = useRef<Record<string, CustomizationProjectEditOptions>>({});
  const projectEditOptionsRequestKeyRef = useRef("");
  const projectEditOptionsLoadingKeyRef = useRef("");
  const projectPointNamesCacheRef = useRef<Record<string, string[]>>({});
  const projectPointNamesPendingRef = useRef<Record<string, Promise<string[]>>>({});
  const projectPointNamesRequestRef = useRef(0);

  useEffect(() => {
    loadProjectEditOptionsRef.current = runtime.loadProjectEditOptions;
    loadProjectPointNamesRef.current = runtime.loadProjectPointNames;
  }, [runtime.loadProjectEditOptions, runtime.loadProjectPointNames]);
  const selectedVersionId = runtime.displayDemand?.id ?? runtime.currentDemand?.id;
  const currentVersion = useMemo(
    () =>
      selectedVersionId
        ? runtime.demandVersions.find((version) => version.id === selectedVersionId) ?? null
        : null,
    [runtime.demandVersions, selectedVersionId],
  );
  const demandGroups = useMemo(
    () => buildCustomizationMobileDemandGroups(runtime.displayDemand, runtime.project),
    [runtime.displayDemand, runtime.project],
  );
  const editModel = useMemo(
    () => buildCustomizationMobileEditModel(runtime.displayDemand, runtime.project),
    [runtime.displayDemand, runtime.project],
  );

  useEffect(() => {
    if (!project?.id) {
      projectEditOptionsRequestKeyRef.current = "";
      projectEditOptionsLoadingKeyRef.current = "";
      setProjectEditOptions(null);
      setProjectEditOptionsLoading(false);
      return;
    }
    const requestKey = [
      project.id,
      project.primarySchoolId ?? "",
      project.customerId ?? "",
    ].join(":");
    const cachedOptions = projectEditOptionsCacheRef.current[requestKey];
    if (cachedOptions) {
      projectEditOptionsRequestKeyRef.current = requestKey;
      projectEditOptionsLoadingKeyRef.current = "";
      setProjectEditOptions(cachedOptions);
      setProjectEditOptionsLoading(false);
      return;
    }
    if (projectEditOptionsLoadingKeyRef.current === requestKey) {
      return;
    }
    let active = true;
    projectEditOptionsRequestKeyRef.current = requestKey;
    projectEditOptionsLoadingKeyRef.current = requestKey;
    setProjectEditOptionsLoading(true);
    const pendingRequest =
      mobileProjectEditOptionsPendingRequests.get(requestKey) ??
      loadProjectEditOptionsRef.current({
        customerId: project.customerId,
        primarySchoolId: project.primarySchoolId,
      })
        .then((result) => result ?? null)
        .finally(() => {
          mobileProjectEditOptionsPendingRequests.delete(requestKey);
        });
    if (!mobileProjectEditOptionsPendingRequests.has(requestKey)) {
      mobileProjectEditOptionsPendingRequests.set(requestKey, pendingRequest);
    }
    void pendingRequest
      .then((result) => {
        if (!active || projectEditOptionsRequestKeyRef.current !== requestKey) {
          return;
        }
        if (result) {
          projectEditOptionsCacheRef.current[requestKey] = result;
        }
        setProjectEditOptions(result ?? null);
      })
      .finally(() => {
        if (active && projectEditOptionsRequestKeyRef.current === requestKey) {
          projectEditOptionsLoadingKeyRef.current = "";
          setProjectEditOptionsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [project?.customerId, project?.id, project?.primarySchoolId]);

  useEffect(() => {
    projectPointNamesRequestRef.current += 1;
    projectPointNamesCacheRef.current = {};
    projectPointNamesPendingRef.current = {};
    setProjectPointNames([]);
    setProjectPointNamesLoading(false);
  }, [project?.id]);

  const loadProjectPointNames = (keyword = "") => {
    if (!project?.id) {
      return;
    }
    const normalizedKeyword = keyword.trim();
    const stayAreaNames = (editModel.stayAreaNames ?? [])
      .map((item) => String(item).trim())
      .filter(Boolean)
      .filter((item, index, all) => all.indexOf(item) === index);
    const stayAreaCacheKey = stayAreaNames.join("\u0001");
    const cacheKey = `${normalizedKeyword || "__initial__"}::${stayAreaCacheKey || "__no_stay__"}`;
    const requestId = ++projectPointNamesRequestRef.current;
    const cached = projectPointNamesCacheRef.current[cacheKey];
    if (cached) {
      setProjectPointNames(cached);
      setProjectPointNamesLoading(false);
      return;
    }
    setProjectPointNamesLoading(true);
    const pending =
      projectPointNamesPendingRef.current[cacheKey] ??
      loadProjectPointNamesRef.current({
        keyword: normalizedKeyword || undefined,
        stayAreaNames: stayAreaNames.length ? stayAreaNames : undefined,
      })
        .then((result) => {
          projectPointNamesCacheRef.current[cacheKey] = result;
          return result;
        })
        .finally(() => {
          delete projectPointNamesPendingRef.current[cacheKey];
        });
    projectPointNamesPendingRef.current[cacheKey] = pending;
    void pending
      .then((result) => {
        if (projectPointNamesRequestRef.current === requestId) {
          setProjectPointNames(result);
        }
      })
      .finally(() => {
        if (projectPointNamesRequestRef.current === requestId) {
          setProjectPointNamesLoading(false);
        }
      });
  };

  const displayRequiredStatus = getCustomizationMobileRequiredStatus(
    runtime.displayDemand,
    runtime.project,
  );
  const persistedRequiredStatus = getCustomizationMobileRequiredStatus(
    runtime.persistedDisplayDemand,
    runtime.persistedProject,
  );
  const hasCurrentDemand = Boolean(runtime.persistedDisplayDemand);
  const generationBaseVersionSeq = runtime.persistedDisplayDemand?.versionSeq ?? 0;
  const isPersistedDemandIncomplete =
    hasCurrentDemand && persistedRequiredStatus.status !== "complete";
  const isDisplayDemandIncomplete =
    hasCurrentDemand && displayRequiredStatus.status !== "complete";
  const canUpdateIncompleteV1 = isPersistedDemandIncomplete && generationBaseVersionSeq === 1;
  const hasVersionChanges = runtime.hasManualDraft || runtime.selectedTraceIds.length > 0;
  const versionSubmitDisabled =
    !hasVersionChanges ||
    (isDisplayDemandIncomplete && generationBaseVersionSeq > 1);
  const versionSubmitAction = canUpdateIncompleteV1 ? "update_current" : "create_version";
  const nextVersionSeq = Math.max(
    generationBaseVersionSeq + 1,
    ...runtime.demandVersions.map((version) => version.versionSeq + 1),
  );
  const versionSubmitLabel =
    versionSubmitAction === "update_current" ? "更新版本 V1" : `创建版本 V${nextVersionSeq}`;
  const shouldConfirmHistoricalBaseGeneration =
    Boolean(runtime.historicalGenerationBase) &&
    versionSubmitAction === "create_version" &&
    !isPersistedDemandIncomplete;
  const versionChangedFieldKeys = buildCustomizationMobileDemandGroups(
    runtime.persistedDisplayDemand,
    runtime.persistedProject,
  )
    .flatMap((group) => group.fields)
    .filter((field) => field.changed)
    .map((field) => field.key);
  const viewModel: CustomizationMobileWorkbenchViewModel = {
    currentVersion,
    demandGroups,
    demandSummary: runtime.displayDemand?.demandSummary?.trim() || "暂无需求摘要",
    displayDemandId: runtime.displayDemand?.id,
    disableDispatcherEdit: Boolean(runtime.historicalGenerationBase),
    dispatcherUserId: runtime.project?.dispatcherUserId,
    dispatcherName: runtime.project?.dispatcherUserName?.trim() || "未指定计调",
    dispatcherPhone: runtime.project?.dispatcherUserPhone?.trim() || undefined,
    editModel,
    errorMessage: runtime.errorMessage || runtime.mutationErrorMessage,
    hasManualDraft: runtime.hasManualDraft,
    manualDraftFieldCount: runtime.manualDraftFieldCount,
    manualDraftPatch: runtime.manualDraftPatch,
    itineraryModifierName: currentVersion?.itineraryModifiedByName?.trim() || "",
    loading: runtime.loading,
    notice,
    notifying: runtime.dispatcherNotifying,
    historicalBaseVersionNumber: runtime.historicalGenerationBase?.versionNumber,
    projectEditOptions,
    projectEditOptionsLoading,
    projectPointNames,
    projectPointNamesLoading,
    loadProjectPointNames,
    demandFieldOptions: runtime.demandFieldOptions,
    projectFieldOptions: runtime.projectFieldOptions,
    projectCode: runtime.displayOrderVersionKey || runtime.project?.legacyId || runtime.project?.projectCode || "未生成",
    currentOrderVersionKey: currentVersion?.orderVersionKey || runtime.displayOrderVersionKey || "",
    projectId: runtime.project?.id ?? projectId,
    projectName: runtime.project?.name?.trim() || "需求定制",
    salesName: runtime.project?.salesUserName?.trim() || "未指定",
    requiredStatus: {
      status: displayRequiredStatus.status,
      text: displayRequiredStatus.text,
      title: displayRequiredStatus.title,
    },
    persistedRequiredStatus: {
      status: persistedRequiredStatus.status,
      text: persistedRequiredStatus.text,
      title: persistedRequiredStatus.title,
    },
    opportunityBinding: runtime.opportunityBinding,
    opportunityBindingOpen: runtime.opportunityBindingOpen,
    opportunityBindingPreparing: runtime.opportunityBindingPreparing,
    selectedVersionId,
    shouldConfirmHistoricalBaseGeneration,
    saving: runtime.projectSaving,
    versionSubmitDisabled,
    versionSubmitLabel,
    versionChangedFieldKeys,
    versionSeq: generationBaseVersionSeq,
    versionSubmitting: runtime.generating,
    updatedAt: runtime.displayDemand?.updatedAt || runtime.displayDemand?.createdAt || "未更新",
    versions: runtime.demandVersions,
    versionSwitching: runtime.isDemandVersionSwitching,
  };

  return {
    confirmedTraceIds: runtime.selectedTraceIds,
    setConfirmedTraceIds: runtime.setSelectedTraceIds,
    sharedRuntime: runtime,
    notifyDispatcher: async (actionText?: string) => {
      if (!runtime.project?.dispatcherUserId?.trim()) {
        setNotice({
          message: "请先在需求定制中填写计调人员，再通知计调。",
          title: "请填写计调人员",
        });
        return;
      }
      if (runtime.selectedTraceIds.length > 0) {
        return;
      }
      const result = await runtime.notifyDispatcher("mobile_customization", actionText);
      if (!result) {
        return;
      }
      setNotice(resolveNotificationNotice(result));
    },
    saveProject: async (payload: CustomizationProjectUpdatePayload) =>
      runtime.saveProject(
        buildMobileManualDraftDelta(
          payload,
          buildCustomizationMobileEditModel(
            runtime.persistedDisplayDemand,
            runtime.persistedProject,
          ),
        ),
        { optimistic: true },
      ),
    createDemandVersion: async () => {
      if (versionSubmitDisabled) {
        return null;
      }
      const baseDemandId =
        versionSubmitAction === "create_version" ? runtime.historicalGenerationBase?.id : undefined;
      return runtime.generateDemandVersion(
        versionSubmitAction,
        baseDemandId ? { baseDemandId } : undefined,
      );
    },
    createNewCustomization: runtime.createNewCustomization,
    startItineraryGeneration: runtime.startItineraryGeneration,
    selectVersion: runtime.selectDemandVersion,
    viewModel,
  };
}

/** 将改回当前可见版本原值的字段标记为删除，避免残留无效草稿。 */
function buildMobileManualDraftDelta(
  payload: CustomizationProjectUpdatePayload,
  baseline: CustomizationProjectUpdatePayload,
): CustomizationProjectUpdatePayload {
  const result = { ...payload } as Record<string, unknown>;
  for (const key of Object.keys(payload) as Array<keyof CustomizationProjectUpdatePayload>) {
    if (isMobileDraftValueEqual(payload[key], baseline[key])) {
      result[key] = undefined;
    }
  }
  return result as CustomizationProjectUpdatePayload;
}

/** 比较手机草稿与版本基线值，兼容数组和空值。 */
function isMobileDraftValueEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    const leftItems = Array.isArray(left) ? left : [];
    const rightItems = Array.isArray(right) ? right : [];
    return leftItems.length === rightItems.length && leftItems.every((item, index) => item === rightItems[index]);
  }
  return (left ?? undefined) === (right ?? undefined);
}

/** 将通知接口结果转换成手机页提示。 */
function resolveNotificationNotice(
  result: CustomizationDispatcherNotificationResult,
): CustomizationMobileWorkbenchNotice {
  if (result.failedCount > 0 && result.sentCount === 0) {
    const failureMessage = result.failureMessages[0]?.trim();
    return {
      message: failureMessage
        ? `微信通知发送失败：${failureMessage}`
        : "微信通知发送失败，请稍后重试。",
      title: "通知失败",
    };
  }
  if (result.failedCount > 0) {
    return {
      message: `已通知 ${result.sentCount} 人，${result.failedCount} 人发送失败。`,
      title: "部分发送失败",
    };
  }
  if (result.skippedCount > 0 && result.sentCount === 0) {
    const failureMessage = result.failureMessages[0]?.trim();
    return {
      message: failureMessage || "当前项目第一计调未配置微信 OpenID，未发送通知。",
      title: "通知未发送",
    };
  }
  return {
    message: "已通知计调查看最新需求。",
    title: "通知已发送",
  };
}
